/**
 * shop-wishlist.js — FlorixSports Wishlist Page
 * ─────────────────────────────────────────────────────
 * Storage key: 'db_wishlist' → string[] of product handles
 *
 * 1. Read handles from localStorage
 * 2. Fetch each /products/${handle}.js in parallel
 * 3. Render product cards into #wlGrid
 * 4. Heart button → remove from wishlist + animate card out
 * 5. Trash button → same as heart
 * 6. "Add to Bag" → POST /cart/add.js (first available variant)
 *    If multiple variants → navigate to PDP for size selection
 * 7. Update count label + header wishlist badge
 * 8. React to db:wishlist:change events from other pages (e.g. product page)
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'db_wishlist';
  var page        = document.getElementById('shopWishlistPage');
  if (!page) return;

  var gridEl      = document.getElementById('wlGrid');
  var emptyEl     = document.getElementById('wlEmpty');
  var skeletonsEl = document.getElementById('wlSkeletons');
  var countEl     = document.getElementById('wlCount');

  /* ──────────────────────────────────────────────
     localStorage helpers
     ────────────────────────────────────────────── */
  function getHandles() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch (e) { return []; }
  }

  function saveHandles(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      window.dispatchEvent(new CustomEvent('db:wishlist:change', { detail: { list: list } }));
    }
    catch (e) {}
  }

  function removeHandle(handle) {
    var list = getHandles().filter(function (h) { return h !== handle; });
    saveHandles(list);
    return list;
  }

  /* ──────────────────────────────────────────────
     UI state helpers
     ────────────────────────────────────────────── */
  function showSkeletons() {
    if (skeletonsEl) { skeletonsEl.removeAttribute('aria-hidden'); }
    if (emptyEl)     { emptyEl.setAttribute('aria-hidden', 'true'); }
    if (gridEl)      { gridEl.style.display = 'none'; }
  }

  function hideSkeletons() {
    if (skeletonsEl) { skeletonsEl.setAttribute('aria-hidden', 'true'); }
  }

  function showEmpty() {
    hideSkeletons();
    if (emptyEl) { emptyEl.removeAttribute('aria-hidden'); }
    if (gridEl)  { gridEl.style.display = 'none'; }
    updateCount(0);
  }

  function showGrid() {
    hideSkeletons();
    if (emptyEl) { emptyEl.setAttribute('aria-hidden', 'true'); }
    if (gridEl)  { gridEl.style.display = ''; }
  }

  function updateCount(n) {
    if (!countEl) return;
    countEl.textContent = n > 0 ? '(' + n + ' ' + (n === 1 ? 'item' : 'items') + ')' : '';
  }

  /* Header wishlist badge (optional — update if present) */
  function updateWishlistBadge(n) {
    var badge = document.querySelector('[data-wishlist-count]');
    if (!badge) return;
    badge.textContent = n;
    badge.setAttribute('data-wishlist-count', n);
    badge.style.display = n > 0 ? 'flex' : 'none';
  }

  /* ──────────────────────────────────────────────
     Fetch product by handle
     /products/${handle}.js returns Shopify product JSON
     ────────────────────────────────────────────── */
  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.js')
      .then(function (res) {
        if (!res.ok) return null;
        return res.json();
      })
      .catch(function () { return null; });
  }

  /* ──────────────────────────────────────────────
     Money formatting
     Shopify prices are integers (pence)
     ────────────────────────────────────────────── */
  function formatMoney(pence) {
    return '\u00a3' + (parseInt(pence, 10) / 100).toFixed(2);
  }

  /* ──────────────────────────────────────────────
     Escape HTML
     ────────────────────────────────────────────── */
  function esc(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ──────────────────────────────────────────────
     Badge logic
     ────────────────────────────────────────────── */
  function getBadge(product) {
    var compareAt = parseInt(product.compare_at_price, 10);
    var price     = parseInt(product.price, 10);
    if (compareAt > price) return { label: 'Sale', cls: 'shop-wl__card-badge--sale' };
    if (product.tags && product.tags.indexOf('new') !== -1) return { label: 'New', cls: 'shop-wl__card-badge--new' };
    if (product.tags && product.tags.indexOf('bestseller') !== -1) return { label: 'Bestseller', cls: 'shop-wl__card-badge--default' };
    if (product.tags && product.tags.indexOf('limited') !== -1) return { label: 'Limited', cls: 'shop-wl__card-badge--default' };
    return null;
  }

  /* ──────────────────────────────────────────────
     Render a single card's HTML
     ────────────────────────────────────────────── */
  function renderCard(product) {
    var price     = parseInt(product.price, 10);
    var compareAt = parseInt(product.compare_at_price, 10) || 0;
    var isOnSale  = compareAt > price;
    var discount  = isOnSale ? Math.round(((compareAt - price) / compareAt) * 100) : 0;
    var badge     = getBadge(product);
    var imgSrc    = product.featured_image || '';
    var pdpUrl    = '/products/' + product.handle;

    /* Find first available variant for "Add to Bag" */
    var firstVariant   = (product.variants && product.variants[0]) || null;
    var multiVariant   = product.variants && product.variants.length > 1;

    var badgeHtml = badge
      ? '<span class="shop-wl__card-badge ' + badge.cls + '">' + esc(badge.label) + '</span>'
      : '';

    var imgHtml = imgSrc
      ? '<img class="shop-wl__card-img" src="' + esc(imgSrc) + '" alt="' + esc(product.title) + '" loading="lazy" width="400" height="533">'
      : '<div class="shop-wl__card-img" style="background:hsl(36,12%,90%)"></div>';

    var compareHtml = isOnSale
      ? '<span class="shop-wl__card-compare">' + formatMoney(compareAt) + '</span>'
        + '<span class="shop-wl__card-discount">-' + discount + '%</span>'
      : '';

    var typeLabel = product.product_type
      ? '<p class="shop-wl__card-type">' + esc(product.product_type) + '</p>'
      : '';

    return '<div class="shop-wl__card" data-handle="' + esc(product.handle) + '" data-variant-id="' + (firstVariant ? firstVariant.id : '') + '" data-multi-variant="' + (multiVariant ? 'true' : 'false') + '" data-pdp="' + esc(pdpUrl) + '">'
      /* Image */
      + '<a href="' + esc(pdpUrl) + '" class="shop-wl__card-img-link" aria-label="' + esc(product.title) + '">'
      +   imgHtml
      +   badgeHtml
      +   '<button class="shop-wl__card-heart" data-handle="' + esc(product.handle) + '" type="button" aria-label="Remove from wishlist">'
      +     '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>'
      +   '</button>'
      + '</a>'
      /* Body */
      + '<div class="shop-wl__card-body">'
      +   typeLabel
      +   '<a href="' + esc(pdpUrl) + '" class="shop-wl__card-name">' + esc(product.title) + '</a>'
      +   '<div class="shop-wl__card-prices">'
      +     '<span class="shop-wl__card-price">' + formatMoney(price) + '</span>'
      +     compareHtml
      +   '</div>'
      +   '<div class="shop-wl__card-actions">'
      +     '<button class="shop-wl__card-add-btn" data-handle="' + esc(product.handle) + '" data-variant-id="' + (firstVariant ? firstVariant.id : '') + '" data-multi-variant="' + (multiVariant ? 'true' : 'false') + '" data-pdp="' + esc(pdpUrl) + '" type="button" aria-label="Add ' + esc(product.title) + ' to bag">'
      +       '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>'
      +       ' Add to Bag'
      +     '</button>'
      +     '<button class="shop-wl__card-remove-btn" data-handle="' + esc(product.handle) + '" type="button" aria-label="Remove from wishlist">'
      +       '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>'
      +     '</button>'
      +   '</div>'
      + '</div>'
      + '</div>';
  }

  /* ──────────────────────────────────────────────
     Remove a card from the grid with animation
     ────────────────────────────────────────────── */
  function animateRemoveCard(handle) {
    var cardEl = gridEl && gridEl.querySelector('.shop-wl__card[data-handle="' + handle + '"]');
    if (cardEl) {
      cardEl.classList.add('is-removing');
      setTimeout(function () {
        if (cardEl.parentNode) cardEl.parentNode.removeChild(cardEl);
        /* Check if grid is now empty */
        var remaining = gridEl ? gridEl.querySelectorAll('.shop-wl__card').length : 0;
        if (remaining === 0) showEmpty();
        else updateCount(remaining);
      }, 280);
    }
    var remaining = removeHandle(handle);
    updateWishlistBadge(remaining.length);
  }

  /* ──────────────────────────────────────────────
     Add to cart AJAX
     ────────────────────────────────────────────── */
  function addToCartAjax(variantId, btn) {
    btn.disabled = true;
    btn.textContent = 'Adding\u2026';

    fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
    })
    .then(function (res) { return res.json(); })
    .then(function () {
      btn.textContent = 'Added!';
      btn.classList.add('is-added');

      /* Update cart badge */
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var badge = document.querySelector('[data-cart-count]');
          if (badge) {
            badge.textContent = cart.item_count;
            badge.setAttribute('data-cart-count', cart.item_count);
            badge.style.display = cart.item_count > 0 ? 'flex' : 'none';
          }
        });

      setTimeout(function () {
        btn.disabled = false;
        btn.classList.remove('is-added');
        btn.innerHTML = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg> Add to Bag';
      }, 2000);
    })
    .catch(function (err) {
      console.error('[shop-wishlist] Add to cart error:', err);
      btn.disabled = false;
      btn.textContent = 'Add to Bag';
    });
  }

  /* ──────────────────────────────────────────────
     Event delegation on the grid
     ────────────────────────────────────────────── */
  if (gridEl) {
    gridEl.addEventListener('click', function (e) {

      /* Heart button on image */
      var heartBtn = e.target.closest('.shop-wl__card-heart');
      if (heartBtn) {
        e.preventDefault();
        var handle = heartBtn.getAttribute('data-handle');
        if (handle) animateRemoveCard(handle);
        return;
      }

      /* Trash remove button */
      var removeBtn = e.target.closest('.shop-wl__card-remove-btn');
      if (removeBtn) {
        var handle = removeBtn.getAttribute('data-handle');
        if (handle) animateRemoveCard(handle);
        return;
      }

      /* "Add to Bag" button */
      var addBtn = e.target.closest('.shop-wl__card-add-btn');
      if (addBtn) {
        var isMulti   = addBtn.getAttribute('data-multi-variant') === 'true';
        var variantId = addBtn.getAttribute('data-variant-id');
        var pdpUrl    = addBtn.getAttribute('data-pdp');

        if (isMulti) {
          /* Multiple sizes → go to PDP where user can pick size */
          window.location.href = pdpUrl;
        } else if (variantId) {
          addToCartAjax(variantId, addBtn);
        }
      }

    });
  }

  /* ──────────────────────────────────────────────
     Main init — fetch + render
     ────────────────────────────────────────────── */
  function init() {
    var handles = getHandles();

    if (handles.length === 0) {
      showEmpty();
      return;
    }

    showSkeletons();

    Promise.all(handles.map(fetchProduct))
      .then(function (products) {
        /* Filter nulls (products no longer available) */
        var validProducts = products.filter(Boolean);

        if (validProducts.length === 0) {
          showEmpty();
          return;
        }

        /* Build and inject grid HTML */
        var html = validProducts.map(renderCard).join('');
        if (gridEl) gridEl.innerHTML = html;

        showGrid();
        updateCount(validProducts.length);
        updateWishlistBadge(validProducts.length);

        /* Clean up any stale handles (products that returned null) */
        var validHandles = validProducts.map(function (p) { return p.handle; });
        saveHandles(validHandles);
      })
      .catch(function (err) {
        console.error('[shop-wishlist] Failed to load products:', err);
        showEmpty();
      });
  }

  /* ──────────────────────────────────────────────
     React to wishlist changes from other tabs/pages
     ────────────────────────────────────────────── */
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) init();
  });

  init();

})();
